import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, ArrowRight, ArrowLeft, Upload } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { n: 1, title: 'Bedrijfsinformatie', desc: 'Vertel ons over uw bedrijf' },
  { n: 2, title: 'Marketing doelen', desc: 'Wat wilt u bereiken?' },
  { n: 3, title: 'Diensten', desc: 'Welke diensten heeft u nodig?' },
  { n: 4, title: 'Assets upload', desc: 'Logo en materialen' },
  { n: 5, title: 'Account koppelingen', desc: 'Koppel uw advertentieaccounts' },
  { n: 6, title: 'Budget & planning', desc: 'Budget en startdatum' },
];

export default function OnboardingPage() {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    api('get', '/api/portal/onboarding').then(r => {
      if (r.data) {
        setData(r.data);
        if (r.data.step) setStep(Math.min(r.data.step + 1, 6));
      }
    }).catch(console.error);
  }, []);

  const save = async (nextStep, isComplete = false) => {
    setSaving(true);
    try {
      await api('post', '/api/portal/onboarding', { ...data, step: nextStep, completed: isComplete });
      if (isComplete) setCompleted(true);
      else setStep(nextStep);
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  const set = (field, value) => setData(d => ({ ...d, [field]: value }));

  const inp = (label, field, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-sm font-medium text-[#94a3b8] mb-2">{label}</label>
      <input type={type} value={data[field] || ''} onChange={e => set(field, e.target.value)} placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017] transition-all" />
    </div>
  );

  if (completed) return (
    <div className="max-w-2xl mx-auto text-center py-16">
      <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-400" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-4">Onboarding voltooid!</h1>
      <p className="text-[#64748b] mb-8">Bedankt! Ons team controleert uw gegevens. U ontvangt binnenkort een uitnodiging voor de kick-off meeting.</p>
      <a href="/platform" className="bg-[#D4A017] text-[#0f172a] font-bold px-8 py-4 rounded-xl inline-block hover:bg-[#B8860B] transition-all">Naar dashboard</a>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-white mb-6">Onboarding</h1>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {STEPS.map(s => (
          <div key={s.n} className="flex items-center gap-2 flex-shrink-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              s.n < step ? 'bg-[#D4A017] text-[#0f172a]' : s.n === step ? 'bg-[#D4A017] text-[#0f172a] ring-4 ring-[#D4A017]/30' : 'bg-white/10 text-[#475569]'
            }`}>
              {s.n < step ? <CheckCircle size={14} /> : s.n}
            </div>
            {s.n < STEPS.length && <div className={`w-8 h-0.5 ${s.n < step ? 'bg-[#D4A017]' : 'bg-white/10'}`} />}
          </div>
        ))}
      </div>

      {/* Step title */}
      <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-8 mb-4">
        <h2 className="text-xl font-bold text-white mb-1">{STEPS[step - 1].title}</h2>
        <p className="text-[#64748b] mb-6">{STEPS[step - 1].desc}</p>

        {/* Step 1: Bedrijfsinformatie */}
        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {inp('Bedrijfsnaam *', 's1_company', 'text', 'Uw bedrijfsnaam')}
            {inp('Website', 's1_website', 'url', 'https://uwbedrijf.nl')}
            {inp('Contactpersoon *', 's1_contact', 'text', 'Voor- en achternaam')}
            {inp('Telefoon *', 's1_phone', 'tel', '06-12345678')}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#94a3b8] mb-2">Branche</label>
              <select value={data.s1_industry || ''} onChange={e => set('s1_industry', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4A017]">
                <option value="">Selecteer branche</option>
                {['E-commerce','Zakelijke dienstverlening','Bouw & Installatie','Horeca & Retail','Zorg','Onderwijs','Tech / SaaS','Vastgoed','Overig'].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Marketing doelen */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-[#94a3b8] text-sm">Selecteer uw primaire marketingdoelen (meerdere mogelijk)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {['Meer leads genereren','Meer online verkopen','Betere vindbaarheid (SEO)','Merkbekendheid vergroten','Klantretentie verhogen','Nieuwe markten betreden'].map(goal => (
                <label key={goal} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${(data.s2_goals || []).includes(goal) ? 'border-[#D4A017] bg-[#D4A017]/10' : 'border-white/10 hover:border-white/20'}`}>
                  <input type="checkbox" checked={(data.s2_goals || []).includes(goal)}
                    onChange={e => set('s2_goals', e.target.checked ? [...(data.s2_goals || []), goal] : (data.s2_goals || []).filter(g => g !== goal))}
                    className="w-4 h-4 accent-[#D4A017]" />
                  <span className="text-white text-sm">{goal}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#94a3b8] mb-2">Aanvullende toelichting</label>
              <textarea value={data.s2_notes || ''} onChange={e => set('s2_notes', e.target.value)} rows={3} placeholder="Beschrijf uw specifieke doelen..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017] resize-none" />
            </div>
          </div>
        )}

        {/* Step 3: Diensten */}
        {step === 3 && (
          <div className="space-y-3">
            <p className="text-[#94a3b8] text-sm">Welke diensten zijn van toepassing?</p>
            {['Website (laten maken / redesign)','SEO & Vindbaarheid','Google Ads (Search / Display)','Meta Ads (Facebook / Instagram)','LinkedIn Ads','TikTok Ads','Lead Generatie / Funnel','Marketing Strategie','Advertising Setup'].map(svc => (
              <label key={svc} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${(data.s3_services || []).includes(svc) ? 'border-[#D4A017] bg-[#D4A017]/10' : 'border-white/10 hover:border-white/20'}`}>
                <input type="checkbox" checked={(data.s3_services || []).includes(svc)}
                  onChange={e => set('s3_services', e.target.checked ? [...(data.s3_services || []), svc] : (data.s3_services || []).filter(s => s !== svc))}
                  className="w-4 h-4 accent-[#D4A017]" />
                <span className="text-white text-sm">{svc}</span>
              </label>
            ))}
          </div>
        )}

        {/* Step 4: Assets */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-[#D4A017]/50 transition-all">
              <Upload className="w-10 h-10 text-[#475569] mx-auto mb-3" />
              <p className="text-white font-medium mb-1">Upload bestanden</p>
              <p className="text-[#64748b] text-sm mb-4">Logo, afbeeldingen, huisstijl, teksten</p>
              <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.zip"
                onChange={async e => {
                  const formData = new FormData();
                  for (const f of e.target.files) formData.append('file', f);
                  toast.success(`${e.target.files.length} bestand(en) geselecteerd`);
                  set('s4_files_noted', true);
                }}
                className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="bg-[#D4A017] text-[#0f172a] font-bold px-6 py-2 rounded-lg cursor-pointer hover:bg-[#B8860B] transition-all text-sm">
                Bestanden selecteren
              </label>
            </div>
            <Link to="/portal/files" className="block text-center text-[#D4A017] text-sm hover:underline">Of ga naar de bestanden-sectie →</Link>
            {inp('Website URL van bestaande assets (optioneel)', 's4_assets_url', 'url', 'https://...')}
          </div>
        )}

        {/* Step 5: Account koppelingen */}
        {step === 5 && (
          <div className="space-y-4">
            <p className="text-[#94a3b8] text-sm">Koppel uw advertentieaccounts. Dit kan ook later via Instellingen.</p>
            {[
              { key: 'google_ads', label: 'Google Ads', desc: 'Voer uw Google Ads klant-ID in (bijv. 123-456-7890)' },
              { key: 'google_analytics', label: 'Google Analytics', desc: 'Measurement ID (bijv. G-XXXXXXXXXX)' },
              { key: 'meta_ads', label: 'Meta Ads (Facebook/Instagram)', desc: 'Ad Account ID (bijv. act_123456789)' },
              { key: 'linkedin_ads', label: 'LinkedIn Ads', desc: 'Campaign Manager account ID' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="bg-white/5 rounded-xl p-4">
                <label className="block text-white font-medium text-sm mb-1">{label}</label>
                <p className="text-[#475569] text-xs mb-2">{desc}</p>
                <input value={data[`s5_${key}`] || ''} onChange={e => set(`s5_${key}`, e.target.value)}
                  placeholder="Voer ID in..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-[#475569] focus:outline-none focus:border-[#D4A017]" />
              </div>
            ))}
            <p className="text-[#475569] text-xs">Alle gegevens worden veilig opgeslagen en alleen gebruikt voor het leveren van onze diensten.</p>
          </div>
        )}

        {/* Step 6: Budget & planning */}
        {step === 6 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#94a3b8] mb-2">Maandelijks advertentiebudget</label>
              <select value={data.s6_budget || ''} onChange={e => set('s6_budget', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4A017]">
                <option value="">Selecteer budget</option>
                {['Onder €500','€500 – €1.000','€1.000 – €2.500','€2.500 – €5.000','€5.000 – €10.000','€10.000+'].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            {inp('Gewenste startdatum', 's6_start_date', 'date')}
            <div>
              <label className="block text-sm font-medium text-[#94a3b8] mb-2">Aanvullende opmerkingen</label>
              <textarea value={data.s6_notes || ''} onChange={e => set('s6_notes', e.target.value)} rows={4}
                placeholder="Specifieke wensen, deadlines of andere informatie die wij moeten weten..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017] resize-none" />
            </div>
            <div className="bg-[#D4A017]/10 border border-[#D4A017]/20 rounded-xl p-4 text-sm text-[#94a3b8]">
              Na het voltooien van de onboarding ontvangt u een bevestigingsmail en plannen we een kick-off meeting.
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
          className="flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-[#64748b] hover:text-white disabled:opacity-30 transition-all">
          <ArrowLeft size={16} /> Vorige
        </button>
        {step < 6 ? (
          <button onClick={() => save(step + 1)} disabled={saving}
            className="flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl transition-all disabled:opacity-60">
            {saving ? 'Opslaan...' : 'Volgende'} <ArrowRight size={16} />
          </button>
        ) : (
          <button onClick={() => save(6, true)} disabled={saving}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-xl transition-all disabled:opacity-60">
            {saving ? 'Verzenden...' : 'Onboarding afronden'} <CheckCircle size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
